import React, { useState, useEffect } from 'react';
import { Plus, Tag, Edit2, Trash2, X, ArrowUp, ArrowDown, Package } from 'lucide-react';
import { createCategory, updateCategory, deleteCategory, reorderCategory } from '../hooks/useStoreApi';
import { AdminTabEmpty } from './store/admin/AdminTabEmpty';

// Category modal kept internal to encapsulate logic
const CategoryModal = ({ storeId, category, onClose, onSave, chatId }) => {
    const [formData, setFormData] = useState({
        name: category?.name || '',
        description: category?.description || '',
        inventory_enabled: !!category?.inventory?.enabled,
        inventory_quantity: category?.inventory?.enabled ? (category?.inventory?.quantity ?? '') : '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const categoryData = { name: formData.name, description: formData.description };
            if (formData.inventory_enabled) {
                const qtyVal = parseInt(formData.inventory_quantity, 10);
                if (isNaN(qtyVal) || qtyVal < 0) {
                    throw new Error('מלאי קטגוריה חייב להיות מספר אי-שלילי');
                }
                categoryData.inventory = { enabled: true, quantity: qtyVal };
            } else {
                categoryData.inventory = { enabled: false };
            }
            if (category) {
                await updateCategory(storeId, category.category_id, categoryData, chatId);
            } else {
                await createCategory(storeId, categoryData, chatId);
            }
            onSave();
        } catch (err) {
            setError(err.message || 'שגיאה בשמירת הקטגוריה');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-ink-strong">{category ? 'ערוך קטגוריה' : 'הוסף קטגוריה חדשה'}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-surface-muted rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {error && <div className="mb-4 p-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-700 text-sm">{error}</div>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">שם הקטגוריה</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-ink mb-1">תיאור</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                            rows="2"
                        />
                    </div>
                    <div className="border-t border-line pt-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-ink">
                            <input
                                type="checkbox"
                                checked={formData.inventory_enabled}
                                onChange={(e) => setFormData(prev => ({ ...prev, inventory_enabled: e.target.checked }))}
                                className="h-4 w-4 rounded border-line-strong text-warning-600 focus:ring-warning-500"
                            />
                            <span>נהל מלאי משותף לקטגוריה</span>
                        </label>
                        <p className="text-xs text-ink-subtle mt-1 leading-tight">
                            כל מוצר שנקנה מקטגוריה זו יקטין את המלאי המשותף בכמות שנרכשה. כאשר המלאי מתאפס, כל המוצרים בקטגוריה יסומנו כאזלו מהמלאי.
                        </p>
                        {formData.inventory_enabled && (
                            <div className="mt-2">
                                <label className="block text-sm font-medium text-ink mb-1">כמות מלאי לקטגוריה</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formData.inventory_quantity}
                                    onChange={(e) => setFormData(prev => ({ ...prev, inventory_quantity: e.target.value }))}
                                    className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-warning-500 focus:border-transparent text-ink-strong"
                                    placeholder="הכנס כמות"
                                    required
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-line-strong rounded-lg hover:bg-surface-muted transition-colors text-ink-strong"
                            disabled={loading}
                        >
                            ביטול
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors text-white disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'שומר...' : (category ? 'שמור' : 'הוסף')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const CategoriesTab = ({ storeId, categories, refetch, onRegisterRefresh, chatId, intent, onIntentConsumed }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);

    useEffect(() => {
        if (onRegisterRefresh) {
            onRegisterRefresh('categories', async () => {
                await refetch?.();
            });
        }
    }, [onRegisterRefresh, refetch]);

    // External create-intent: open the create modal when the AI chat sent us here.
    useEffect(() => {
        if (intent === 'create') {
            setEditingCategory(null);
            setShowModal(true);
            onIntentConsumed?.();
        }
    }, [intent, onIntentConsumed]);

    const handleDelete = async (categoryId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הקטגוריה?')) return;
        try {
            await deleteCategory(storeId, categoryId, chatId);
            refetch();
        } catch (err) {
            alert('שגיאה במחיקת הקטגוריה: ' + err.message);
        }
    };

    const handleReorder = async (categoryId, direction) => {
        try {
            await reorderCategory(storeId, categoryId, direction, chatId);
            refetch();
        } catch (err) {
            alert('שגיאה בשינוי סדר הקטגוריה: ' + err.message);
        }
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-line">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-ink-strong">ניהול קטגוריות</h2>
                    <button
                        onClick={() => { setEditingCategory(null); setShowModal(true); }}
                        className="flex items-center gap-2 px-4 py-2 bg-warning-500 text-white rounded-lg hover:bg-warning-600 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        <span>הוסף קטגוריה</span>
                    </button>
                </div>
                {!categories || categories.length === 0 ? (
                    <AdminTabEmpty
                        icon={Tag}
                        tone="info"
                        title="קטגוריות עוזרות ללקוחות למצוא מהר"
                        body="כשיש בחנות יותר מ-5 מוצרים, ארגון לקטגוריות הופך את החוויה למקצועית והרבה יותר נוחה. הלקוח רואה את התפריט שלך כמו חנות אמיתית, לא רשימה ארוכה."
                        examples={[
                            'מאפיה — לחמים · עוגיות · עוגות · ימי הולדת',
                            'ירקן — ירקות · פירות · עשבי תיבול · ביצים',
                            'בוטיק — חולצות · מכנסיים · אקססוריז · מבצעים',
                        ]}
                        primaryAction={{
                            label: 'ליצור קטגוריה ראשונה',
                            onClick: () => { setEditingCategory(null); setShowModal(true); },
                        }}
                    />
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {categories.map((category, index) => {
                            const isFirst = index === 0;
                            const isLast = index === categories.length - 1;
                            return (
                                <div key={category.category_id} className="flex items-center justify-between p-4 border border-line rounded-lg hover:bg-surface-muted">
                                    <div className="flex items-center gap-3 flex-1">
                                        <div className="flex flex-col gap-1">
                                            <button
                                                onClick={() => handleReorder(category.category_id, 'up')}
                                                disabled={isFirst}
                                                className={`p-1 rounded transition-all ${isFirst
                                                    ? 'text-ink-subtle cursor-not-allowed'
                                                    : 'text-info-600 hover:bg-info-50'
                                                }`}
                                                title="הזז למעלה"
                                            >
                                                <ArrowUp className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleReorder(category.category_id, 'down')}
                                                disabled={isLast}
                                                className={`p-1 rounded transition-all ${isLast
                                                    ? 'text-ink-subtle cursor-not-allowed'
                                                    : 'text-info-600 hover:bg-info-50'
                                                }`}
                                                title="הזז למטה"
                                            >
                                                <ArrowDown className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-ink-strong">{category.name}</h3>
                                            {category.description && <p className="text-sm text-ink-muted">{category.description}</p>}
                                            {category.inventory?.enabled && (
                                                <p className={`text-xs mt-1 flex items-center gap-1 font-medium ${category.inventory.quantity > 0 ? 'text-info-700' : 'text-danger-600'}`}>
                                                    <Package className="w-3 h-3" />
                                                    <span>מלאי משותף: {category.inventory.quantity ?? 0}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => { setEditingCategory(category); setShowModal(true); }}
                                            className="p-2 text-info-600 hover:bg-info-50 rounded-lg"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(category.category_id)}
                                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {showModal && (
                <CategoryModal
                    storeId={storeId}
                    category={editingCategory}
                    onClose={() => { setShowModal(false); setEditingCategory(null); }}
                    onSave={() => { setShowModal(false); setEditingCategory(null); refetch(); }}
                    chatId={chatId}
                />
            )}
        </div>
    );
};

export default CategoriesTab;
